// Controle de documentos de técnicos/empresas (ASO, NRs, integração…) com data de validade.
// Entram ao validar formulários (perguntas "pedir validade") ou à mão. Excel = relatório de vencimentos.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Avatar, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, MenuItem,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, makeStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ConfirmationModal from "../../components/ConfirmationModal";
import GetAppIcon from "@material-ui/icons/GetApp";
import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import suporteApi, { baixar, mensagemErro } from "../api";
import { DialogCard, dataBR } from "../comum";

const SITUACOES = {
  VENCIDO: ["Vencido", "#c62828"], VENCE_30: ["Vence em até 30 dias", "#ef6c00"], VENCE_60: ["Vence em até 60 dias", "#f9a825"],
  VIGENTE: ["Vigente", "#2e7d32"], SEM_VALIDADE: ["Sem validade", "#757575"],
};
const SUGESTOES = ["ASO", "NR-10", "NR-35", "NR-33", "NR-12", "INTEGRAÇÃO", "CNH", "CERTIFICADO TÉCNICO"];

const useStyles = makeStyles((theme) => ({
  tile: { padding: 12, cursor: "pointer", borderLeft: "5px solid", height: "100%" },
  numero: { fontSize: 28, fontWeight: 600 },
  lista: { flex: 1, overflow: "auto", ...theme.scrollbarStyles },
  tabela: { "& td, & th": { fontSize: 13, padding: "6px 8px" } },
  filtros: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", margin: "8px 0" },
}));

const ChipSituacao = ({ d }) => {
  const [rot, cor] = SITUACOES[d.situacao] || [d.situacao, "#757575"];
  const dias = d.dias === null ? "" : d.dias < 0 ? ` há ${-d.dias} dia(s)` : ` · ${d.dias} dia(s)`;
  const texto = d.dias === 0 ? "Vence hoje" : `${rot}${d.situacao === "VIGENTE" || d.situacao === "SEM_VALIDADE" ? "" : dias}`;
  return <Chip size="small" label={texto}
    style={{ background: cor, color: "#fff", height: 22 }} />;
};

// técnico / candidato / contato: busca enquanto digita, com a foto do WhatsApp
const iniciais = (n) => (n || "?").replace(/^QRA\s*\d+\s*·\s*/i, "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase();
const BuscaPessoa = ({ valor, onEscolher }) => {
  const [opcoes, setOpcoes] = useState([]);
  const [texto, setTexto] = useState(valor || "");
  const [carregando, setCarregando] = useState(false);
  useEffect(() => {
    if (texto.trim().length < 2) return setOpcoes([]);
    setCarregando(true);
    const t = setTimeout(() => {
      suporteApi.get("/api/documentos/pessoas", { params: { busca: texto } })
        .then((r) => setOpcoes(r.data)).catch(() => {}).finally(() => setCarregando(false));
    }, 300);
    return () => clearTimeout(t);
  }, [texto]);
  return (
    <Autocomplete
      freeSolo options={opcoes} loading={carregando} filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.nome)}
      inputValue={texto}
      onInputChange={(e, v) => { setTexto(v); onEscolher({ nome: v }); }}
      onChange={(e, v) => { if (v && typeof v !== "string") onEscolher(v); }}
      renderOption={(o) => (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Avatar src={o.foto || undefined} style={{ width: 34, height: 34, fontSize: 14 }}>{iniciais(o.nome)}</Avatar>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{o.nome}</div>
            {o.empresa && <div style={{ fontSize: 12.5 }}>{`🏢 ${o.empresa}`}</div>}
            <div style={{ fontSize: 12, opacity: 0.7 }}>{[o.origem, o.detalhe].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
      )}
      renderInput={(params) => (
        <TextField {...params} label="Técnico / responsável *" placeholder="Digite nome, QRA ou empresa" fullWidth size="small"
          variant="outlined" margin="dense" />
      )}
    />
  );
};

const DialogDocumento = ({ doc, tipos, onClose, onSalvo }) => {
  const [f, setF] = useState(doc);
  const [arquivos, setArquivos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const campo = (nome) => ({ value: f[nome] || "", onChange: (e) => setF({ ...f, [nome]: e.target.value }),
    fullWidth: true, size: "small", variant: "outlined", margin: "dense" });
  const salvar = async () => {
    setSalvando(true);
    try {
      if (f.id) {
        await suporteApi.put(`/api/documentos/${f.id}`, {
          pessoa: f.pessoa, empresa: f.empresa || null, documento: f.documento, emissao: f.emissao || null,
          validade: f.validade || null, observacao: f.observacao || null, ativo: f.ativo !== false,
        });
        if (arquivos.length) {
          const fd = new FormData();
          arquivos.forEach((a) => fd.append("arquivos", a));
          await suporteApi.post(`/api/documentos/${f.id}/arquivos`, fd);
        }
      } else {
        const fd = new FormData();
        ["pessoa", "empresa", "documento", "emissao", "validade", "observacao"].forEach((k) => fd.append(k, f[k] || ""));
        arquivos.forEach((a) => fd.append("arquivos", a));
        await suporteApi.post("/api/documentos", fd);
      }
      toast.success("Documento salvo");
      onSalvo();
      onClose();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setSalvando(false);
  };
  const opcoes = [...new Set([...SUGESTOES, ...tipos])];
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{f.id ? "Editar documento" : "Novo documento"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <BuscaPessoa valor={f.pessoa} onEscolher={(p) => setF((x) => ({ ...x, pessoa: p.nome, empresa: x.empresa || p.empresa || "" }))} />
          </Grid>
          <Grid item xs={12} sm={6}><TextField label="Empresa" {...campo("empresa")} /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Documento *" {...campo("documento")}>
              {opcoes.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}><TextField label="Emissão" type="date" InputLabelProps={{ shrink: true }} {...campo("emissao")} /></Grid>
          <Grid item xs={6} sm={3}><TextField label="Validade" type="date" InputLabelProps={{ shrink: true }} {...campo("validade")} /></Grid>
          <Grid item xs={12}><TextField label="Observação" multiline {...campo("observacao")} /></Grid>
          <Grid item xs={12}>
            {(f.arquivos || []).map((a) => (
              <Chip key={a.arquivo} size="small" label={`📄 ${a.nome}`} clickable component="a" href={a.url} target="_blank"
                rel="noopener noreferrer" style={{ margin: 2 }} />
            ))}
            <Button size="small" variant="outlined" color="primary" component="label" style={{ marginLeft: 4 }}>
              {arquivos.length ? `${arquivos.length} arquivo(s) para anexar` : "📎 Anexar arquivo"}
              <input type="file" hidden multiple accept="application/pdf,image/*,.doc,.docx"
                onChange={(e) => setArquivos(Array.from(e.target.files || []))} />
            </Button>
          </Grid>
          {f.id && (
            <Grid item xs={12}>
              <TextField select label="Situação do registro" {...campo("ativo")} value={f.ativo === false ? "nao" : "sim"}
                onChange={(e) => setF({ ...f, ativo: e.target.value === "sim" })}>
                <MenuItem value="sim">Ativo (no controle)</MenuItem>
                <MenuItem value="nao">Arquivado (substituído ou fora de uso)</MenuItem>
              </TextField>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={salvando || !f.pessoa || !f.documento} onClick={salvar}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

const ControleDocumentos = () => {
  const c = useStyles();
  const [dados, setDados] = useState({ itens: [], resumo: null, tipos: [] });
  const [f, setF] = useState({ busca: "", documento: "", situacao: "" });
  const [termo, setTermo] = useState("");
  const [editando, setEditando] = useState(null);
  const [card, setCard] = useState(null);
  const [excluindo, setExcluindo] = useState(null);
  const excluir = async () => {
    try {
      await suporteApi.delete(`/api/documentos/${excluindo.id}`);
      toast.success("Documento excluído");
      carregar();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setTermo(f.busca), 350);
    return () => clearTimeout(t);
  }, [f.busca]);
  const params = { busca: termo, documento: f.documento, situacao: f.situacao };
  const carregar = useCallback(async () => {
    try {
      const { data } = await suporteApi.get("/api/documentos", { params: { busca: termo, documento: f.documento, situacao: f.situacao } });
      setDados(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  }, [termo, f.documento, f.situacao]);
  useEffect(() => {
    carregar();
  }, [carregar]);

  const r = dados.resumo || {};
  const tiles = [["VENCIDO", r.vencidos], ["VENCE_30", r.vence_30], ["VENCE_60", r.vence_60], ["VIGENTE", r.vigentes], ["SEM_VALIDADE", r.sem_validade]];

  return (
    <MainContainer>
      <MainHeader>
        <Title>{`Controle de documentos (${r.total ?? 0})`}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setEditando({ pessoa: "", documento: "" })}>
            Novo documento
          </Button>
          <Button startIcon={<GetAppIcon />} onClick={() => baixar("/api/documentos/excel", params).catch((err) => toast.error(mensagemErro(err)))}>
            Excel (vencimentos)
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Grid container spacing={1}>
        {tiles.map(([k, n]) => (
          <Grid item xs={6} sm={4} md key={k}>
            <Paper variant="outlined" className={c.tile} style={{ borderLeftColor: SITUACOES[k][1], opacity: f.situacao && f.situacao !== k ? 0.55 : 1 }}
              onClick={() => setF({ ...f, situacao: f.situacao === k ? "" : k })}>
              <Typography variant="body2" color="textSecondary">{SITUACOES[k][0]}</Typography>
              <div className={c.numero}>{n ?? 0}</div>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Paper variant="outlined" className={c.filtros}>
        <TextField size="small" variant="outlined" value={f.busca} onChange={(e) => setF({ ...f, busca: e.target.value })}
          placeholder="Buscar técnico, empresa, documento ou observação" style={{ flex: "1 1 300px" }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <TextField select size="small" variant="outlined" label="Documento" value={f.documento} style={{ minWidth: 180 }}
          onChange={(e) => setF({ ...f, documento: e.target.value })}>
          <MenuItem value="">Todos</MenuItem>
          {dados.tipos.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        {(f.situacao || f.documento || f.busca) && <Button size="small" onClick={() => setF({ busca: "", documento: "", situacao: "" })}>Limpar filtros</Button>}
      </Paper>
      <Paper variant="outlined" className={c.lista}>
        <Table size="small" stickyHeader className={c.tabela}>
          <TableHead>
            <TableRow>
              <TableCell>Técnico / responsável</TableCell><TableCell>Empresa</TableCell><TableCell>Documento</TableCell>
              <TableCell>Validade</TableCell><TableCell>Situação</TableCell><TableCell>Arquivos</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!dados.itens.length && (
              <TableRow><TableCell colSpan={7}><Typography variant="body2" color="textSecondary">
                Nenhum documento. Eles entram ao validar formulários com "pedir data de validade" ou pelo botão Novo documento.
              </Typography></TableCell></TableRow>
            )}
            {dados.itens.map((d) => (
              <TableRow key={d.id} hover>
                <TableCell><b>{d.pessoa}</b></TableCell>
                <TableCell>{d.empresa || "—"}</TableCell>
                <TableCell>{d.documento}</TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>{d.validade ? dataBR(d.validade, true) : "—"}</TableCell>
                <TableCell><ChipSituacao d={d} /></TableCell>
                <TableCell>
                  {d.arquivos.map((a, i) => (
                    <a key={a.arquivo} href={a.url} target="_blank" rel="noopener noreferrer" style={{ marginRight: 6 }}>{`📄 ${i + 1}`}</a>
                  ))}
                </TableCell>
                <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                  {d.atendimento_id && <Button size="small" onClick={() => setCard(d.atendimento_id)}>Card</Button>}
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditando(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Excluir"><IconButton size="small" onClick={() => setExcluindo(d)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {editando && <DialogDocumento doc={editando} tipos={dados.tipos} onClose={() => setEditando(null)} onSalvo={carregar} />}
      <DialogCard id={card} open={!!card} onClose={() => setCard(null)} />
      <ConfirmationModal title={excluindo ? `Excluir ${excluindo.documento} de ${excluindo.pessoa}?` : ""} open={!!excluindo}
        onClose={() => setExcluindo(null)} onConfirm={excluir}>
        O documento sai do controle de vez. Para guardar o histórico (ex.: NR substituída pela nova), use Editar → Arquivado.
      </ConfirmationModal>
    </MainContainer>
  );
};

export default ControleDocumentos;
